import { useEffect, useState, useRef } from "react";
import { getAllTests, getComparableResultsAll } from "../../services/Model";
import TestHeader from "./TestHeaders";
import TestFooter from "./TestFooter";
import AllTestResults from "./AllResults";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import { generateReportPDF } from "../../utils/pdfExport";

const AllReports = ({ print }) => {
  const [Tests, setTests] = useState([]);
  const [AllTests, setAllTests] = useState();
  const [HeadInfo, setHeadInfo] = useState();
  const [FooterInfo, setFooterInfo] = useState();
  const [pdfTriggered, setPdfTriggered] = useState(false);
  const { pid, tid } = useParams();
  const printRef = useRef();
  const navigate = useNavigate();

  const generatePDF = () => {
    generateReportPDF(printRef, HeadInfo, pid, tid);
  };

  // Auto-generate PDF when print route loads (QR code scan)
  useEffect(() => {
    if (print && Tests.length > 0 && AllTests && HeadInfo && !pdfTriggered) {
      setPdfTriggered(true);
      generateReportPDF(printRef, HeadInfo, pid, tid);
    }
  }, [print, Tests, AllTests, HeadInfo, pdfTriggered, pid, tid]);

  useEffect(() => {
    const fetchAllTests = async () => {
      const res = await getAllTests(pid, tid);
      const data = res.data;

      const headerInfo = {
        patientInfo: data.header.patientInfo,
        testInfo: data.header.testInfo,
        categories: data.categories,
      };
      setHeadInfo(headerInfo);

      setFooterInfo({ labTechnician: data.header.testInfo?.labTechnician });

      const gender = data.header.patientInfo.gender;
      const categories = data.categories.map((cat) => ({
        categoryName: cat.categoryName,
        tests: cat.tests.map((test) => {
          const filtered = test.parameters
            .filter(
              (p) =>
                (p.resultGender === "N" ||
                  p.resultGender === gender ||
                  p.resultGender === null) &&
                (p.ageRange === "N" ||
                  p.ageRange === "A" ||
                  p.ageRange === null),
            )
            .map((p) => ({
              ...p,
              testId: data.header.testInfo.testId,
              doTest: data.header.testInfo.dateOfTest,
            }));
          const grouped = filtered.reduce((r, p) => {
            r[p.parameterCode] = r[p.parameterCode] || [];
            r[p.parameterCode].push(p);
            return r;
          }, {});
          const paramRemarks = filtered.map((p) => p.remarks).filter(Boolean);
          return {
            testName: test.testName,
            testCode: test.testCode,
            result: grouped,
            gender,
            resultRecordingDate: test.resultRecordingDate,
            remarks: [...new Set(paramRemarks)],
          };
        }),
      }));

      setTests(categories);
      fetchAllComparable();
    };
    fetchAllTests();

    const fetchAllComparable = async () => {
      const c = await getComparableResultsAll(pid);
      setAllTests(c.data.rows);
    };
  }, [pid, tid]);

  return (
    <div>
      <Helmet>
        <title>FOH Lab Online - Test Results</title>
      </Helmet>
      {HeadInfo && (
        <div ref={printRef}>
          <TestHeader
            header={HeadInfo}
            print={print}
            pid={pid}
            printRef={printRef}
          />
          <div
            className={`print-body${Tests.length > 0 && AllTests ? " pdf-ready" : ""}`}
            style={{ margin: print ? "0 10px" : "0 30px" }}
          >
            {Tests.map((cat, ci) => (
              <div
                key={ci}
                className="pdf-category"
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <p
                  style={{
                    textAlign: "left",
                    marginBottom: "10px",
                    fontSize: "16px",
                  }}
                >
                  <strong>{cat.categoryName}</strong>
                </p>
                {(cat.tests || []).map((t, ti) => (
                  <div key={ti} className="pdf-test">
                    <p className="text-center" style={{ marginBottom: "5px" }}>
                      <strong>{t.testName}</strong>
                    </p>
                    {AllTests && (
                      <AllTestResults
                        results={t.result}
                        resultDate={t.resultRecordingDate}
                        oldResults={AllTests.filter(
                          (a) =>
                            a.TESTCODE === t.testCode &&
                            (a.RESULT_GENDER === "N" ||
                              a.RESULT_GENDER === t.gender) &&
                            (a.AGERANGE === "N" || a.AGERANGE === "A"),
                        )}
                      />
                    )}
                    {t.remarks?.length > 0 && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6c757d",
                          fontStyle: "italic",
                          margin: "5px 0",
                          whiteSpace: "pre-line",
                        }}
                      >
                        <strong>Remarks:</strong>
                        {"\n"}
                        {t.remarks.join("\n")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {HeadInfo?.testInfo?.remarks && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  fontStyle: "italic",
                  margin: "10px 0",
                  whiteSpace: "pre-line",
                }}
              >
                <strong>Remarks:</strong>
                {"\n"}
                {HeadInfo.testInfo.remarks}
              </div>
            )}
            {FooterInfo && <TestFooter footer={FooterInfo} />}
          </div>
        </div>
      )}

      {!print && (
        <div className="text-center my-4 no-print">
          <button
            title="Generate PDF Report"
            className="btn btn-success btn-lg me-2"
            onClick={generatePDF}
          >
            <i className="fas fa-file-pdf me-2"></i>Download PDF
          </button>
          <button
            title="Close"
            className="btn btn-outline-secondary btn-lg"
            onClick={() => navigate("/")}
          >
            <i className="fas fa-times me-2"></i>Close
          </button>
        </div>
      )}
    </div>
  );
};

export default AllReports;
